import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TreeTable } from 'primereact/treetable';
import { Column } from 'primereact/column';
import moment from 'moment';
import { getBranches, filterBillsList, formatDateForApi } from '../../utilities/api';
import { buildCashierSalesTree } from '../../utilities/branchAnalysis';
import { WAITER_LEVEL_COLORS, WAITER_LEVEL_ICONS } from '../../utilities/sessionSalesTree';
import useUser from '../../hooks/useUser';
import './billreports.css';

const ALL_BRANCHES_OPTION = { branch_id: 'All', branch_name: 'All Branches' };

const formatCurrency = (amount) => {
    const num = parseFloat(amount || 0);
    return num.toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
};

// Backend sends dates as DD-MM-YYYY (occasionally YYYY-MM-DD) — parse explicitly with those
// formats rather than moment's ambiguous auto-parse, which misreads "11-09-2026" as
// MM-DD-YYYY and swaps day/month.
const formatDate = (dateString) => {
    if (!dateString) return '';
    const parsed = moment(dateString, ['DD-MM-YYYY', 'YYYY-MM-DD'], true);
    return parsed.isValid() ? parsed.format('DD-MM-YYYY') : dateString;
};

/**
 * Standalone Cashier Sales page — Day -> [Branch ->] Cashier -> Waiter tree, with Bill
 * Starting / Bill Ending / Total No. of Bills / Sales Bill Amount per node. Reads the same
 * bill-level rows Branch Analysis uses (filterBillsList), since invoice numbers only exist at
 * the bill level. Same page structure/behavior as Session Sales: own filters, own Apply for
 * Admin/CEO, auto-fetch for Manager, tree starts collapsed.
 */
const CashierSalesContent = () => {
    const { userRole } = useUser();
    const rolename = (userRole || localStorage.getItem('role') || '').toUpperCase();
    const isManager = rolename === 'MANAGER';

    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(new Date());
    const [branches, setBranches] = useState([]);
    const [branchesLoading, setBranchesLoading] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [billRows, setBillRows] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState({});

    // Branch list — Manager is locked to their own branch, Admin/CEO can pick or see All.
    useEffect(() => {
        if (isManager) {
            const branchId = localStorage.getItem('branchid') || '';
            const branchName = localStorage.getItem('branchname') || branchId;
            const managerBranch = { branch_id: branchId, branch_name: branchName };
            setBranches([managerBranch]);
            setSelectedBranch(managerBranch);
            return;
        }
        const clientId = localStorage.getItem('clientid');
        if (!clientId) return;
        setBranchesLoading(true);
        getBranches(clientId)
            .then((list) => {
                const withAll = [ALL_BRANCHES_OPTION, ...(Array.isArray(list) ? list : [])];
                setBranches(withAll);
                setSelectedBranch(withAll[0]);
            })
            .catch(() => {
                setBranches([ALL_BRANCHES_OPTION]);
                setSelectedBranch(ALL_BRANCHES_OPTION);
            })
            .finally(() => setBranchesLoading(false));
    }, [isManager]);

    const buildPayload = () => {
        const branchId = selectedBranch?.branch_id || 'All';
        const payload = {
            from_date: fromDate ? formatDateForApi(fromDate) : '',
            to_date: toDate ? formatDateForApi(toDate) : '',
            branch_id: branchId,
            status: ''
        };
        const clientId = localStorage.getItem('clientid');
        if (clientId) payload.client_id = clientId;
        return payload;
    };

    const fetchReport = async () => {
        if (!fromDate || !toDate) return;
        setLoading(true);
        try {
            const rows = await filterBillsList(buildPayload());
            setBillRows(Array.isArray(rows) ? rows : []);
        } catch (error) {
            console.error('Error loading Cashier Sales report:', error);
            setBillRows([]);
        } finally {
            setLoading(false);
        }
    };

    // Manager has no Apply button — auto-fetch once the branch resolves. Admin/CEO get dates
    // defaulted to today but must click Apply themselves before any data is fetched.
    useEffect(() => {
        if (isManager && selectedBranch && fromDate && toDate) fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isManager, selectedBranch]);

    const cashierTreeNodes = useMemo(() => buildCashierSalesTree(Array.isArray(billRows) ? billRows : [], formatDate), [billRows]);

    // Day rows start collapsed — same as Session Sales — only the date parent shows by
    // default, and the user expands a date manually (via the row's expander arrow) to drill
    // into Branch/Cashier/Waiter.

    const totalBranches = useMemo(() => new Set((billRows || []).map((r) => String(r.branch_id ?? r.branch ?? '').trim() || 'UNKNOWN')).size, [billRows]);
    const totalCashiers = useMemo(() => new Set((billRows || []).map((r) => String(r.userId ?? r.waiter_no ?? '').trim() || 'UNKNOWN')).size, [billRows]);

    const labelBody = (node) => {
        const { label, level } = node.data;
        return (
            <span className={`sbw-tree-label sbw-tree-label-${level}`}>
                <i className={`pi ${WAITER_LEVEL_ICONS[level]} mr-2`} style={{ color: WAITER_LEVEL_COLORS[level] }} />
                {label || 'Unassigned'}
            </span>
        );
    };

    const invoiceBody = (field) => (node) => <span className={`sbw-tree-amount sbw-tree-amount-${node.data.level}`}>{node.data[field] || '-'}</span>;
    const billsBody = (node) => <span className={`sbw-tree-amount sbw-tree-amount-${node.data.level}`}>{node.data.totalBills}</span>;
    const salesBody = (node) => <span className={`sbw-tree-amount sbw-tree-amount-${node.data.level}`}>₹{formatCurrency(node.data.totalSales)}</span>;

    const rowClassName = (node) => ({ [`sbw-tree-row-${node.data.level}`]: true });

    return (
        <div className="bill-reports-container">
            <div className="mb-4">
                <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--primary-color)' }}>
                    Cashier Sales
                </h2>
                <p className="text-color-secondary">Day / Branch / Cashier / Waiter, with Bill Starting, Bill Ending, Total No. of Bills, and Sales Bill Amount.</p>
            </div>

            <Card className="mb-4 bill-reports-filter-card">
                <div className="flex flex-wrap align-items-end bill-reports-filter-row" style={{ gap: '1rem' }}>
                    {!isManager && (
                        <>
                            <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                                <label htmlFor="cs-from">
                                    From Date <span className="text-red-500">*</span>
                                </label>
                                <Calendar id="cs-from" value={fromDate} onChange={(e) => setFromDate(e.value)} dateFormat="dd-mm-yy" showIcon placeholder="From Date" className="w-full" showButtonBar />
                            </div>
                            <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                                <label htmlFor="cs-to">
                                    To Date <span className="text-red-500">*</span>
                                </label>
                                <Calendar id="cs-to" value={toDate} onChange={(e) => setToDate(e.value)} dateFormat="dd-mm-yy" showIcon placeholder="To Date" minDate={fromDate} className="w-full" showButtonBar />
                            </div>
                        </>
                    )}
                    <div className="flex flex-column" style={{ flex: '1 1 200px', minWidth: '180px' }}>
                        <label htmlFor="cs-branch">Branch</label>
                        <Dropdown
                            id="cs-branch"
                            value={selectedBranch}
                            options={branches}
                            onChange={(e) => setSelectedBranch(e.value)}
                            optionLabel="branch_name"
                            className="w-full"
                            disabled={isManager}
                            loading={branchesLoading || undefined}
                        />
                    </div>
                    {!isManager && (
                        <div className="flex gap-2" style={{ flex: '0 0 auto' }}>
                            <Button label="Apply" icon="pi pi-check" className="p-button-primary" onClick={fetchReport} disabled={!fromDate || !toDate || !selectedBranch || loading} loading={loading} />
                        </div>
                    )}
                </div>
            </Card>

            <Card className="mb-4 sbw-tree-card">
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                    <h3 className="text-xl font-semibold m-0">Cashier Sales</h3>
                    <div className="flex gap-2">
                        {totalBranches > 1 && <Tag value={`${totalBranches} Branches`} style={{ background: WAITER_LEVEL_COLORS.branch }} />}
                        {totalCashiers > 0 && <Tag value={`${totalCashiers} Cashier${totalCashiers === 1 ? '' : 's'}`} severity="info" />}
                    </div>
                </div>
                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                        <ProgressSpinner />
                    </div>
                ) : cashierTreeNodes.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center p-5">
                        <i className="pi pi-users text-5xl text-color-secondary mb-3"></i>
                        <p className="text-color-secondary">{billRows === null ? 'Select a date range and branch, then Apply.' : 'No cashier sales found for the selected filters.'}</p>
                    </div>
                ) : (
                    <TreeTable
                        value={cashierTreeNodes}
                        expandedKeys={expandedKeys}
                        onToggle={(e) => setExpandedKeys(e.value)}
                        rowClassName={rowClassName}
                        className="p-treetable-sm sbw-tree"
                        tableStyle={{ minWidth: '55rem' }}
                    >
                        <Column field="label" header="Day / Branch / Cashier / Waiter" body={labelBody} expander style={{ minWidth: '260px' }} />
                        <Column field="invoiceStart" header="Bill Starting" body={invoiceBody('invoiceStart')} style={{ textAlign: 'center', justifyContent: 'center', minWidth: '150px' }} />
                        <Column field="invoiceEnd" header="Bill Ending" body={invoiceBody('invoiceEnd')} style={{ textAlign: 'center', justifyContent: 'center', minWidth: '150px' }} />
                        <Column field="totalBills" header="Total No. of Bills" body={billsBody} style={{ textAlign: 'center', justifyContent: 'center', minWidth: '150px' }} />
                        <Column field="totalSales" header="Sales Bill Amount" body={salesBody} style={{ textAlign: 'right', justifyContent: 'flex-end', minWidth: '160px' }} />
                    </TreeTable>
                )}
            </Card>
        </div>
    );
};

export default CashierSalesContent;
